// src/utils/permitButtonConfig.ts

// Define types for roles and statuses
export type UserRole = 'User' | 'Manager' | 'Front Desk Manager';
export type ApplicationStatus1 = 'Draft' | 'In Review L1' | 'Revision' | 'In Review L2' | 'Approved' | 'Issued';
export type ButtonAction = 'Update' | 'Proceed to Payment' | 'Send For L1 Review' | 'Send For L2 Review' | 'Send For Revision' | 'Approve';

// Define the button configuration interface
export interface ButtonConfig {
  label: string;
  action: ButtonAction;
  disabled: boolean;
}

/**
 * Returns the button configuration based on application status and user role
 * @param status - Current application status
 * @param role - User role
 * @param isChecking - Optional flag to indicate loading state for payment
 * @returns Array of button configurations
 */
export const getButtonsConfig = (
  status: ApplicationStatus1, 
  role: UserRole,
  isChecking = false
): ButtonConfig[] => {
  // Define available button templates
  const buttons = {
    update: { 
      label: 'Update', 
      action: 'Update', 
      disabled: false 
    },
    proceedToPayment: { 
      label: isChecking ? 'Checking Status...' : 'Proceed To Payment', 
      action: 'Proceed to Payment', 
      disabled: isChecking 
    },
    sendForL1Review: { 
      label: 'Send For L1 Review', 
      action: 'Send For L1 Review', 
      disabled: false 
    },
    sendForL2Review: { 
      label: 'Send For L2 Review', 
      action: 'Send For L2 Review', 
      disabled: false 
    },
    sendForRevision: { 
      label: 'Send For Revision', 
      action: 'Send For Revision', 
      disabled: false 
    },
    approve: { 
      label: 'Approve', 
      action: 'Approve', 
      disabled: false 
    }
  };

  // Map of statuses and roles to button configurations
  const buttonMap = {
    'Draft': {
      'User': [{ ...buttons.update }, { ...buttons.proceedToPayment }],
      'Front Desk Manager': [{ ...buttons.update }, { ...buttons.proceedToPayment }],
      'Manager': []
    },
    'In Review L1': {
      'User': [{ ...buttons.update, disabled: true }],
      'Front Desk Manager': [
        { ...buttons.update }, 
        { ...buttons.sendForL2Review },
        { ...buttons.sendForRevision }
      ],
      'Manager': []
    },
    'Revision': {
      'User': [{ ...buttons.update }, { ...buttons.sendForL1Review }],
      'Front Desk Manager': [
        { ...buttons.update, disabled: true }, 
        { ...buttons.sendForL1Review, disabled: true }, 
        { ...buttons.sendForRevision, disabled: true }
      ],
      'Manager': []
    },
    'In Review L2': {
      'User': [{ ...buttons.update, disabled: true }],
      'Front Desk Manager': [
        { ...buttons.update, disabled: true }, 
        { ...buttons.sendForL2Review, disabled: true },
        { ...buttons.sendForRevision, disabled: true }
      ],
      'Manager': [
        { ...buttons.update }, 
        { ...buttons.approve }, 
        { ...buttons.sendForRevision }
      ]
    },
    'Approved': {
      'User': [{ ...buttons.update, disabled: true }],
      'Front Desk Manager': [{ ...buttons.update, disabled: true }],
      'Manager': [{ ...buttons.update, disabled: true }]
    },
    'Issued': {
      'User': [],
      'Front Desk Manager': [],
      'Manager': []
    }
  };

  // Return the appropriate buttons for this status and role
  return buttonMap[status][role] || [];
};